import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import SelectSmall from '../../general-components/select-small';
import { SelectGroupOption } from '../../general-components/select-group';
import PreviewButton from '../preview-button';
import DirectionalSelection from './directional-selection';
import { GreenroomState as GS } from '../../../stores/types/types';
import * as GraphicsActions from '../../../stores/actions/graphics';
import { AnimationType, GraphicInterface } from '../../../stores/reducers/graphics';
import {
	getAnimationName,
} from '../../../utils/custom-graphics';
import { fixedNumberFromInput } from '../../../utils/utils';
import DurationInput from '../../general-components/duration-input';
import { DURATION_INPUT_TYPES } from '../../../types/constants';
import useShots from '../../../contexts/shots/provider';
import {
	editOverlayDraft,
	updateOverlayInAllShotOverlays,
} from '../../../connection/graphics';

interface Props {
	isForShotEditorModal?: Boolean;
}

export const transitionEffects: SelectGroupOption[] = [
	{ label: 'When shot starts', value: 'on-shot-transition' },
	{ label: 'Manual', value: 'manual' },
	{ label: 'After delay', value: 'after-delay' },
];

export const transitionOutEffects: SelectGroupOption[] = [
	{ label: 'Manual', value: 'manual' },
	{ label: 'After delay', value: 'after-delay' },
];

export const animationOptions: SelectGroupOption[] = [
	{ label: 'None', value: 'none' },
	{ label: 'Dissolve', value: 'dissolve' },
	{ label: 'Move In', value: 'move-in' },
];

export const animationOutOptions: SelectGroupOption[] = [
	{ label: 'None', value: 'none' },
	{ label: 'Dissolve', value: 'dissolve' },
	{ label: 'Move Out', value: 'move-in' },
];

export const durationOptions: SelectGroupOption[] = [
	{ label: '250 ms.', value: 0.25 },
	{ label: '500 ms.', value: 0.5 },
	{ label: '1 sec.', value: 1 },
	{ label: '5 sec.', value: 5 },
	{ label: '10 sec.', value: 10 },
];

export const directionals = [
	'move-in',
];

export default function Animation(props: Props) {
	const { isForShotEditorModal } = props;
	const dispatch = useDispatch();
	const {
		currentGraphicId,
		graphics,
		isPreviewAnimationPlaying,
	} = useSelector((state: GS) => state.GraphicsReducer);
	const shotModalGraphics = useSelector(
		(state: GS) => state.GraphicsReducer.shotModalGraphics,
	);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const enabledOverlays = useSelector(
		(state: GS) => state.GraphicsReducer.enabledOverlays,
	);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);

	let selectedGraphic = isForShotEditorModal
		// eslint-disable-next-line max-len
		? shotModalGraphics.find((g) => g.id === currentGraphicId) || graphics.find((g) => g.id === currentGraphicId)
		: graphics.find((g) => g.id === currentGraphicId);

	if (!selectedGraphic) { // if there is no selectedGraphic pick the first one
		selectedGraphic = isForShotEditorModal
			? shotModalGraphics[0] ?? { animation: {} }
			: graphics[0] ?? { animation: {} };
	}

	const {
		animation = {
			id: 0,
			name: '',
			build_in: 0,
			build_out: 0,
			in_animation: 0,
			in_direction: 0,
			in_duration: 0,
			out_animation: 0,
			out_direction: 0,
			out_duration: 0,
		},
	} = selectedGraphic;

	const [selectedInTransition, setSelectedInTransition] = useState(
		transitionEffects[animation.build_in ?? 0],
	);
	const [selectedInAnimation, setSelectedInAnimation] = useState(
		animationOptions[animation.in_animation ?? 0],
	);
	const [selectedInDuration, setSelectedInDuration] = useState(
		durationOptions[animation.in_duration ?? 0],
	);
	const [selectedInDirection, setSelectedInDirection] = useState(
		animation.in_direction ?? 0,
	);
	const [selectedOutTransition, setSelectedOutTransition] = useState(
		transitionOutEffects[animation.build_out ?? 0],
	);
	const [selectedOutAnimation, setSelectedOutAnimation] = useState(
		animationOutOptions[animation.out_animation ?? 0],
	);
	const [selectedOutDuration, setSelectedOutDuration] = useState(
		durationOptions[animation.out_duration ?? 0],
	);
	const [selectedOutDirection, setSelectedOutDirection] = useState(
		animation.out_direction ?? 0,
	);
	const [currentAnimationData, setCurrentAnimationData] = useState(animation);
	const [showFor, setShowFor] = useState(animation?.show_for ?? 0);
	const [delayInDuration, setDelayInDuration] = useState(animation?.delay_in ?? 0);

	const { isShotBuilderOpen } = useShots();

	useEffect(() => {
		// @ts-ignore
		window.ignoreSave = true;
		setSelectedInTransition(transitionEffects[animation.build_in ?? 0]);
		setSelectedInAnimation(animationOptions[animation.in_animation ?? 0]);
		setSelectedInDuration(durationOptions[animation.in_duration ?? 0]);
		setSelectedInDirection(animation.in_direction ?? 0);
		setSelectedOutTransition(transitionOutEffects[animation.build_out ?? 0]);
		setSelectedOutAnimation(animationOutOptions[animation.out_animation ?? 0]);
		setSelectedOutDuration(durationOptions[animation.out_duration ?? 0]);
		setCurrentAnimationData(animation);
		setShowFor(animation?.show_for ?? 0);
		setDelayInDuration(animation?.delay_in ?? 0);
		const tim = setTimeout(() => {
			// @ts-ignore
			window.ignoreSave = false;
			clearTimeout(tim);
		}, 500);
	}, [currentGraphicId]);

	// save the updates
	useEffect(() => {
		// @ts-ignore
		if (window?.ignoreSave === true) return;
		const updatedAnimation: AnimationType = {
			name: animation.name,
			id: animation.id,
			build_in: transitionEffects.findIndex((o) => o.value === selectedInTransition.value),
			build_out: transitionOutEffects.findIndex((o) => o.value === selectedOutTransition.value),
			in_animation: animationOptions.findIndex((o) => o === selectedInAnimation),
			in_direction: selectedInDirection,
			in_duration: durationOptions.findIndex((o) => o === selectedInDuration),
			out_animation: animationOutOptions.findIndex((o) => o === selectedOutAnimation),
			out_direction: selectedOutDirection,
			out_duration: durationOptions.findIndex((o) => o === selectedOutDuration),
			show_for: showFor,
			delay_in: delayInDuration,
		};
		const keys = Object.keys(updatedAnimation);

		// lets not send allow them to DoS our database
		let noChange = true;
		keys.forEach((key) => {
			// @ts-ignore
			if (currentAnimationData[key] !== updatedAnimation[key]) {
				noChange = false;
			}
		});
		if (!noChange) {
			setCurrentAnimationData(updatedAnimation);
			if (isForShotEditorModal) {
				const newGraphic: any = { ...selectedGraphic, animation: updatedAnimation };
				editOverlayDraft(token ?? '', newGraphic);
			} else if (token) {
				const updatedGraphic: GraphicInterface | undefined = selectedGraphic
					? { ...selectedGraphic, animation: updatedAnimation }
					: undefined;

				if (updatedGraphic) {
					dispatch(GraphicsActions.editGraphicsAction(
						token,
						updatedGraphic,
						(editedGraphics: GraphicInterface) => {
							dispatch(GraphicsActions.updateGraphicsAction(editedGraphics));
						},
					));
				}

				if (meeting?.meeting && updatedGraphic?.id) {
					updateOverlayInAllShotOverlays(
						token,
						meeting.meeting,
						updatedGraphic.id,
						!(selectedInTransition.value === 'manual'),
					);
				}
			}
		}
	}, [
		selectedInTransition,
		selectedInAnimation,
		selectedInDuration,
		selectedInDirection,
		selectedOutTransition,
		selectedOutAnimation,
		selectedOutDuration,
		selectedOutDirection,
		currentAnimationData,
		showFor,
		delayInDuration,
		meeting,
	]);

	const isSelectedGraphicEnabled = useMemo((): boolean => {
		if (enabledOverlays?.length && selectedGraphic?.id) {
			return enabledOverlays.includes(selectedGraphic.id);
		}

		return false;
	}, [enabledOverlays, selectedGraphic]);

	const handlePreviewAnimation = (isOut: boolean) => {
		const triggeredAnimation = isOut
			? selectedOutAnimation
			: selectedInAnimation;

		const shotEditor = isShotBuilderOpen ? '-shot-editor' : '';

		const video = document.getElementById(`custom-graphic-vid-${currentGraphicId}${shotEditor}`) as HTMLVideoElement;
		if (!isOut && video) {
			video.currentTime = 0;
			video.play();
		}

		if (!animation || triggeredAnimation.value === 'none') return;
		const obj = document.getElementById(`graphic-wrapper-${currentGraphicId}${shotEditor}`);
		const child = document.getElementById(`custom-graphic-${currentGraphicId}${shotEditor}`);
		const resizeBox = obj?.parentNode?.childNodes[1] as HTMLElement;

		const duration = isOut
			? selectedOutDuration
			: selectedInDuration;

		if (obj) {
			dispatch(GraphicsActions.previewAnimationIsPlaying(true));
			obj.className = classNames(
				'graphic-wrapper',
				selectedGraphic
					? getAnimationName(selectedGraphic, isOut)
					: '',
			);
			if (child && triggeredAnimation.value === 'move-in' && isSelectedGraphicEnabled) {
				child.style.transform = `rotate(${selectedGraphic?.rotate ?? 0}deg)`;
			}
			if (resizeBox) {
				resizeBox.style.display = 'none';
			}
			setTimeout(() => { // reset it so we can play again
				obj.className = 'graphic-wrapper';
				if (child && isSelectedGraphicEnabled) {
					child.style.transform = '';
				}
				if (resizeBox) {
					resizeBox.style.display = 'block';
				}
				dispatch(GraphicsActions.previewAnimationIsPlaying(false));
			}, (duration.value * 1000) - 100);
		}
	};

	return (
			<div className="settings-container animation-container">
				<div className="animation-row" style={{ marginTop: '1.6rem', marginBottom: '1.6rem' }}>
					<div style={{ fontWeight: 'bold' }}>Build In</div>
					<PreviewButton
						onClick={() => handlePreviewAnimation(false)}
						disabled={isPreviewAnimationPlaying}
					/>
				</div>
				<div className={classNames('animation-row', 'select-full')}>
					<SelectSmall
						options={transitionEffects}
						label={selectedInTransition?.label || transitionEffects[0].label}
						selected={selectedInTransition || transitionEffects[0]}
						dropdown={true}
						onChange={(e) => {
							setSelectedInTransition(
								transitionEffects.find((ao) => ao.value === e) || transitionEffects[0],
							);
						}}
					/>
					{selectedInTransition?.value === 'after-delay' && (
						<DurationInput
							inputType={DURATION_INPUT_TYPES.SECONDS}
							value={delayInDuration}
							onBlur={(value: number) => setDelayInDuration(value)}
							minValue={'0'}
						/>
					)}
				</div>
				<div className="animation-row">
					<label>Effect</label>
					<SelectSmall
						options={animationOptions}
						label={selectedInAnimation?.label || animationOptions[0].label}
						selected={selectedInAnimation || animationOptions[0]}
						dropdown
						onChange={(e) => {
							setSelectedInAnimation(
								animationOptions.find((ao) => ao && ao.value === e) || animationOptions[0],
							);
						}}
						dropdownPosition="drop-left"
					/>
				</div>
				{directionals.includes(selectedInAnimation.value) && (
					<div className="animation-row">
						<div>Direction</div>
						<div>
							<DirectionalSelection
								selectedDirection={selectedInDirection}
								onClick={(e) => {
									setSelectedInDirection(e);
								}}
							/>
						</div>
					</div>
				)}
				{selectedInAnimation?.value !== 'none' && (
					<div className="animation-row">
						<label>Duration</label>
						<SelectSmall
							options={durationOptions}
							label={selectedInDuration?.label || durationOptions[0].label}
							selected={selectedInDuration || durationOptions[0]}
							dropdown
							onChange={(e) => {
								setSelectedInDuration(
									durationOptions.find((ao) => ao.value === e) || durationOptions[0],
								);
							}}
							dropdownPosition="drop-left"
						/>
					</div>
				)}
				{selectedOutTransition?.value === 'after-delay' && (
					<>
						<hr />
						<div className="animation-row">
							<div style={{ maxWidth: '62px', minWidth: '62px' }}>Show for</div>
							<div className="background-scale" style={{ position: 'relative', top: '-0.8rem' }}>
								<input
									className="animation-slider"
									onChange={(e) => {
										setShowFor(fixedNumberFromInput(e.target.value));
									}}
									value={showFor}
									type="range"
									step=".25"
									min=".5"
									max="300"
								/>
								<DurationInput
									inputType={DURATION_INPUT_TYPES.SECONDS_AND_MINUTES}
									value={showFor}
									onBlur={(value: number) => setShowFor(value)}
									minValue={'0'}
								/>
							</div>
						</div>
					</>
				)}
				<hr />
				<div className="animation-row" style={{ marginBottom: '1.6rem' }}>
					<div style={{ fontWeight: 'bold' }}>Build Out</div>
					<PreviewButton
						onClick={() => handlePreviewAnimation(true)}
						disabled={isPreviewAnimationPlaying}
					/>
				</div>
				<div className={classNames('animation-row', 'select-full')}>
					<SelectSmall
						options={transitionOutEffects}
						label={selectedOutTransition?.label || transitionOutEffects[0].label}
						selected={selectedOutTransition || transitionOutEffects[0]}
						dropdown
						onChange={(e) => {
							setSelectedOutTransition(
								transitionOutEffects.find((ao) => ao.value === e) || transitionOutEffects[0],
							);
						}}
					/>
				</div>
				<div className="animation-row">
					<label>Effect</label>
					<SelectSmall
						options={animationOutOptions}
						label={selectedOutAnimation?.label || animationOutOptions[0].label}
						selected={selectedOutAnimation || animationOutOptions[0]}
						dropdown
						onChange={(e) => {
							setSelectedOutAnimation(
								animationOutOptions.find((ao) => ao?.value === e) || animationOutOptions[0],
							);
						}}
						dropdownPosition="drop-left"
					/>
				</div>
				{directionals.includes(selectedOutAnimation?.value) && (
					<div className="animation-row">
						<div>Direction</div>
						<div>
							<DirectionalSelection
								selectedDirection={selectedOutDirection}
								onClick={(e) => {
									setSelectedOutDirection(e);
								}}
							/>
						</div>
					</div>
				)}
				{selectedOutAnimation.value !== 'none' && (
					<div className="animation-row">
						<label>Duration</label>
						<SelectSmall
							options={durationOptions}
							label={selectedOutDuration?.label || durationOptions[0].label}
							selected={selectedOutDuration || durationOptions[0]}
							dropdown
							onChange={(e) => {
								setSelectedOutDuration(
									durationOptions.find((ao) => ao.value === e) || durationOptions[0],
								);
							}}
							dropdownPosition="drop-left"
							forceOpenUp={true}
						/>
					</div>
				)}
			</div>
	);
}
